import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { countries } from '../../countries';
import { useFilterDropdownStyles } from '../../styles';
import { sortByName } from '../../utils';

export const getButtonText = ({
  delimiter,
  i18n,
  placeholder,
  selectedCountries,
}) => {
  if (selectedCountries.length === 0) {
    return placeholder;
  }
  const formattedSelectionText = selectedCountries
    .map(code => i18n.getString(countries[code].name))
    .join(delimiter);
  const countryText = selectedCountries.length > 1 ? i18n.getString(_TL_('Countries:')) : i18n.getString(_TL_('Country:'));
  return `${countryText} ${formattedSelectionText}`;
};

export const CountrySelector = ({
  onSelect,
  selectedCountries,
}) => {
  const styles = useFilterDropdownStyles();

  const i18n = useContext(I18nContext);
  const placeholder = i18n.getString(_TL_('Countries: All'));
  const dropdownLabel = i18n.getString(_TL_('Experiment country filter'));
  const delimiter = i18n.getString(_TL_(', '));

  const buttonText = useMemo(() => getButtonText({
    delimiter,
    i18n,
    placeholder,
    selectedCountries,
  }), [delimiter, i18n, placeholder, selectedCountries]);

  const countryKeyValues = useMemo(() => Object.values(countries).sort(sortByName(i18n)), [i18n]);

  const onOptionSelect = useCallback((e, data) => {
    onSelect(data.selectedOptions);
  }, [onSelect]);

  return (
    <div className={styles.root}>
      <Dropdown
        className="country-selector"
        aria-label={dropdownLabel}
        placeholder={placeholder}
        button={<span className={styles.truncatedText}>{buttonText}</span>}
        data-selectedOptions={selectedCountries.length ? selectedCountries : placeholder}
        multiselect
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedCountries}
      >
        {countryKeyValues.map(({ code, name }) => (
          <Option
            key={code}
            value={code}
            className={`country-selector-option-${code}`}
          >
            {i18n.getString(name)}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
};

CountrySelector.propTypes = {
  onSelect: PropTypes.func.isRequired,
  selectedCountries: PropTypes.arrayOf(PropTypes.string).isRequired,
};
